import type { CSSProperties, ReactNode } from 'react'
import { useT, type TKey } from '@/i18n'
import { cn } from '@/lib/cn'
import { STATUS_PALETTE, statusPillClass, statusPillMinWidth, type StatusColor } from './statusPalette'

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent'

const tonePalette: Record<Tone, StatusColor> = {
  neutral: 'gray',
  success: 'green',
  warning: 'orange',
  danger: 'red',
  info: 'blue',
  accent: 'purple',
}

/**
 * A status or tag label in the client's palette. With a `tone` it is a status
 * and gets a steady minimum width so a column of statuses lines up; without
 * one it is a compact neutral tag ("+2", a type). `dot` is accepted for older
 * call sites and no longer drawn: the palette carries the meaning.
 */
export function Badge({
  tone,
  children,
  className,
}: {
  tone?: Tone
  dot?: boolean
  children: ReactNode
  className?: string
}) {
  const palette = STATUS_PALETTE[tonePalette[tone ?? 'neutral']]
  return (
    <span
      className={cn(statusPillClass, tone ? statusPillMinWidth : 'px-[calc(var(--spacing)*2.5*var(--ui-size))]', className)}
      style={{ '--pill-fg': palette.color, '--pill-bg': palette.background } as CSSProperties}
    >
      {children}
    </span>
  )
}

const statusTone: Record<string, Tone> = {
  active: 'success',
  inactive: 'neutral',
  pending: 'warning',
  blocked: 'danger',
  disabled: 'neutral',
  draft: 'neutral',
  approved: 'success',
  cancelled: 'danger',
  closed: 'neutral',
}

/** Status always renders its text; colour is a secondary cue. */
export function StatusBadge({ status }: { status?: string | boolean | null }) {
  const t = useT()
  const value = typeof status === 'boolean' ? (status ? 'active' : 'inactive') : status || 'unknown'
  const label = value in statusTone || value === 'unknown' || value === 'ok' ? t(`status.${value}` as TKey) : value
  return (
    <Badge tone={statusTone[value] ?? 'neutral'} dot>
      {label}
    </Badge>
  )
}

/**
 * A status label in a colour chosen in a reference, such as a route
 * substatus («Подстатусы маршрута»): "#rrggbb" for the text, a light tint of
 * it behind (the dark theme derives its own tint, as for the palette); the
 * default `tone` when no colour is set.
 */
export function ColorBadge({ color, tone = 'info', children, className }: { color?: string; tone?: Tone; children: ReactNode; className?: string }) {
  if (!color) return <Badge tone={tone} className={className}>{children}</Badge>
  return (
    <span
      className={cn(statusPillClass, statusPillMinWidth, className)}
      style={{ '--pill-fg': color, '--pill-bg': `color-mix(in srgb, ${color} 12%, white)` } as CSSProperties}
    >
      {children}
    </span>
  )
}
